import { find, isEmpty, propEq } from "ramda";
import { getValidNode } from './qRules.node.helpers';

function findParameterInner(name, parameterSet) {
    return find(
        propEq('Key', name),
        parameterSet
    );
}

function findParameter(parameterCollection, name) {
    var parameterArray = parameterCollection.parameterArray;

    if (isEmpty(parameterArray)) {
        // no parameters available
        return null;
    }

    var plainValueElement = findParameterInner(name, parameterArray);
    var plainValue = plainValueElement ? plainValueElement.Value : null;

    var xpathValueElement = findParameterInner(name + '-xpath', parameterArray);
    var xpathValue = xpathValueElement ? xpathValueElement.Value : null;

    var dsNameValueElement = findParameterInner(name + "-dsname", parameterArray);
    var dsNameValue = dsNameValueElement ? dsNameValueElement.Value : null;

    if (plainValue && xpathValue) {
        throw new Error(`${name} and ${name}-xpath cannot both be specified.`);
    }
    if (dsNameValue && !xpathValue) {
        throw new Error(`${name}-dsname cannot be specified unless ${name}-xpath is specified.`);
    }
    if (plainValue === null && xpathValue === null) {
        return null;
    }

    return {
        Name: name,
        Value: plainValue,
        XPath: xpathValue,
        DSName: dsNameValue
    };
}

const getParamValueFromDataSources = (param, dataSources) => {
    const ds = dataSources.getDom(param.DSName);
    const errorMessage = `Could not find valid node for parameter ${param.Name} at XPath ${param.XPath}${param.DSName ? ` in data source ${param.DSName}` : ""}`;
    const node = getValidNode(ds, param.XPath, errorMessage);

    return node.value();
};

const PARAM_UNSPECIFIED = { unspecified: true };

const resolveSpecifiedParamValue = (parameterCollection, key) => {
    const param = findParameter(parameterCollection, key);

    if (!param) {
        return PARAM_UNSPECIFIED;
    }

    if (!param.XPath) {
        return param.Value;
    }

    return getParamValueFromDataSources(param, parameterCollection.dataSources);
};

export const getParamValue = (parameterCollection, key, defaultValue) => {
    const specifiedValue = resolveSpecifiedParamValue(parameterCollection, key);

    if (specifiedValue === PARAM_UNSPECIFIED) {
        return typeof defaultValue === 'undefined'
            ? null
            : defaultValue;
    }

    return specifiedValue;
};

export const hasParam = (parameterCollection, key) => {
    const param = resolveSpecifiedParamValue(parameterCollection, key);

    return param !== PARAM_UNSPECIFIED;
};

const paramStringToBool = (paramValue) => {
    const paramLower = paramValue.toLowerCase();

    return paramLower === "yes" || paramLower === "true" || paramLower === "1";
};

function getBoolParamValue(parameterCollection, key, defaultValue = false) {
    const paramValue = getParamValue(parameterCollection, key, null);

    if (!paramValue) {
        return defaultValue;
    }

    return paramStringToBool(paramValue);
}

function getIntParamValue(parameterCollection, key, defaultValue = 0, allowInvalid = true, allowBlanks = true) {
    const value = getParamValue(parameterCollection, key, null);

    if (allowBlanks && !value) {
        return defaultValue;
    }

    const parsedValue = parseInt(value);

    if (Number.isNaN(parsedValue)) {
        if (allowInvalid) {
            return defaultValue;
        }

        throw new Error(`Value '${value}' for parameter '${key}' is not a valid integer.`);
    }

    return parsedValue;
}

function getDoubleParamValue(parameterCollection, key, defaultValue, allowInvalid, allowBlanks) {
    if (typeof defaultValue === 'undefined') {
        defaultValue = 0;
    }

    if (typeof allowInvalid === 'undefined') {
        allowInvalid = true;
    }

    if (typeof allowBlanks === 'undefined') {
        allowBlanks = true;
    }

    var value = getParamValue(parameterCollection, key, null);

    if (allowBlanks && !value) {
        return defaultValue;
    }

    var doubleValue = parseFloat(value);
    if (Number.isNaN(doubleValue)) {
        if (allowInvalid) {
            return defaultValue;
        }

        throw new Error(String.format("Value '{0}' for parameter '{1}' is not a valid number.", value, key));
    }

    return doubleValue;
}

export const getParamValueOrEnvironmentVariable = (
    parameterCollection,
    paramKey,
    settings,
    environmentVariableKey,
    defaultValue = null,
) => {
    const paramValue = getParamValue(parameterCollection, paramKey, null);

    if (paramValue !== null) {
        return paramValue;
    }

    const environmentValue = settings.getEnvironmentVariable(environmentVariableKey);

    if (environmentValue) {
        return environmentValue.value;
    }

    return defaultValue;
}

export {
    findParameter,
    getBoolParamValue,
    getDoubleParamValue,
    getIntParamValue,
};